import i18n from '@/plugins/i18n';
import { getSymmetryValue, getPrctValue } from '@/helpers/helped-chart-functions.helper';

export enum ESide {
  Left = 'left',
  Right = 'right'
}
// Predefined ranges
export enum ERange {
  R1 = 'range0',
  R2 = 'range1',
  R3 = 'range2',
  R4 = 'range3'
}
// Predefined positions
export enum EPos {
  P0 = 'P0',
  P1 = 'P1',
  P2 = 'P2',
  P3 = 'P3'
}
export type TSvg = string;
export type TImages = {
  readonly [P in ESide]: TSvg;
};
export type TRange = {
  type: ERange;
  min: number;
  max: number;
};
export type TRangeData = {
  [key: string]: any;
  left: {
    [key: string]: any;
    data: any;
    compareData: any;
  };
  right: {
    [key: string]: any;
    data: any;
    compareData: any;
  };
};
export type TAverage = {
  range: number;
  min: number;
  max: number;
  data: TRange[];
};
export type TSubData = {
  [key: string]: any;
  abs: {
    value: {
      avg: number;
    };
    status: string;
  };
  rel: {
    value: {
      avg: number;
    };
    status: string;
  };
};
export type TData = {
  readonly [P in ESide]: TSubData;
};
export type TDatas = {
  [key: string]: any;
  taligrade: TData;
  plantigrade: TData;
  digitigrade: TData;
  heelStrike: TDataExtended;
  flatFoot: TDataExtended;
  heelOff: TDataExtended;
  toesOff: TDataExtended;
};
export type TDataExtended = {
  value: {
    left: number;
    right: number;
  };
} & {
  readonly [P in ERange]: number;
};

// Colors
export const rangeColors: any = {
  neutral: {
    background: 'rgba(37, 192, 144, 0.12)',
    color: '#3ad88e',
    chevron: null
  },
  low: {
    background: 'rgba(255, 189, 26, 0.1)',
    color: 'rgb(255, 179, 26)',
    chevron: 'down'
  },
  high: {
    background: 'rgba(255, 189, 26, 0.1)',
    color: 'rgb(255, 179, 26)',
    chevron: 'up'
  },
  very_low: {
    background: 'rgba(255, 94, 26, 0.1)',
    color: 'rgb(255, 94, 26)',
    chevron: 'down'
  },
  very_high: {
    background: 'rgba(255, 94, 26, 0.1)',
    color: 'rgb(255, 94, 26)',
    chevron: 'up'
  },
  minimum: {
    background: 'rgba(246, 171, 69, 0.13)',
    color: '#f6ab45',
    chevron: 'down'
  },
  maximum: {
    background: 'rgba(246, 171, 69, 0.13)',
    color: '#f6ab45',
    chevron: 'up'
  },
  'extreme-minimum': {
    background: 'rgba(255, 120, 120, 0.12)',
    color: '#ff7878',
    chevron: 'down'
  },
  'extreme-maximum': {
    background: 'rgba(255, 120, 120, 0.12)',
    color: '#ff7878',
    chevron: 'up'
  },
  null: {
    background: 'rgba(110, 118, 141, 0.12)',
    color: '#6e768d',
    chevron: null
  }
};

// Images
export const images: TImages = {
  [ESide.Left]:
    'M1.26 119.15c-.48 5.95-1.36 17 .85 28.81.52 2.78 1.33 6.69 2.35 11.63l1.08 5.22c.08.51 9.42 54.24 10.54 81.68l.1 3.4c0 1.36.05 2.88.1 4.53.09 4.73.14 9.28.19 13.6.11 10.17.2 19 .81 26.45.75 9.22 2.28 17 4.55 23.15a28.59 28.59 0 0 0 1.17 2.95c.39.86.81 1.68 1.23 2.43a56.12 56.12 0 0 0 7.32 10.39l.39.42c8.74 10.07 19.89 15.58 31.16 15.58a36.1 36.1 0 0 0 4.4-.28c19.79-2.54 34.84-21.75 36.6-46.72.16-.89 1.67-10.43-.56-28.9-.19-1.55-.41-3.27-.64-5.12-2.72-21.16-7.77-60.54 4.92-90.81L109 175c7.17-15.74 18-39.52 21.55-64.93 4.19-30.14-6-47-8.33-50.47.18-.24.34-.47.51-.71l.15-.21.27-.37c10.17-14.68 8.16-31.25 2.67-42.15-5-10-13-16.35-20.42-16.18C93.31.26 89.81 7 88.79 10.81c-2.18-2.71-4.68-4.42-7.19-4.17-7.86.78-12.28 9.53-13.72 13-3.13-2.36-7.21-3-11.61-1.77C48.44 20 45.14 31.34 44 37.16a12.76 12.76 0 0 0-2.05-3.5c-2.53-3-6-3.61-9.91-1.82-10.13 4.67-11.06 16.29-11 20.43-5.8-.85-14.5 9-15.49 10.1a20.32 20.32 0 0 0-5.27 10.2A19 19 0 0 0 0 75.06a22 22 0 0 0 0 2.44c.05.81.15 1.61.27 2.41a61.89 61.89 0 0 0 1.93 7.81c.42 1.44.82 2.8 1.15 4.14 1 4.12.56 9-.15 13.43-.28 1.77-.61 3.46-.9 5a43.59 43.59 0 0 0-.83 5.71c-.03.78-.11 1.83-.21 3.15zm39.13-62.36a24.51 24.51 0 0 1-14.94 10.54 23.63 23.63 0 0 0-.39-10.79A6.41 6.41 0 0 0 23 53.11c-.08-2.56.08-15 9.87-19.46 3.06-1.41 5.6-1 7.55 1.31 4.01 4.75 4.02 15.76-.03 21.83zm26.93-35.1c3.27 2.91 4.6 8 3.65 13.92A17.57 17.57 0 0 1 59.29 50c-7 2.59-13.66 4.28-15.28 3.87a26.07 26.07 0 0 0 1-13.1h.36c0-.18 2.34-18.54 11.46-21.1 4.07-1.07 7.8-.38 10.49 2.02zm24.83 0c2.89 9.24 2.59 21-6.91 26.39A31.16 31.16 0 0 1 67.59 47 19.6 19.6 0 0 0 73 35.9c1-6.1-.3-11.45-3.48-14.89 1-2.43 5.09-11.66 12.32-12.38 3.34-.32 7.9 5.42 10.31 13.11zm28.55 36.63c-3.38-1.73-17.53-8.62-31.23-10.77C96.81 41 96.83 30 94.06 21.14a34.84 34.84 0 0 0-3.67-8.07c.12-1.73 1.42-10.77 15-11.07h.23c6.45 0 13.81 6 18.37 15.08 2.66 5.19 10.01 23.34-3.29 41.24zm-117.45 61c.11-1.34.19-2.41.22-3.21a42.74 42.74 0 0 1 .85-5.37c1.07-5.52 2.54-13.07 1-19.27-.34-1.39-.74-2.77-1.17-4.23C2 79.67-.33 71.86 7 63.69c2.53-2.92 10-10.28 13.88-9.42 1.08.24 1.83 1.16 2.31 2.82 2.79 9.78-2.59 22.27-12.25 28.44L12 87.22a30.81 30.81 0 0 0 13.11-18.31l.11.52a26.43 26.43 0 0 0 16.85-11.55 17 17 0 0 0 1.19-2.15 5.26 5.26 0 0 0 1.42.17c4.2 0 12.47-2.94 15.29-4a19.8 19.8 0 0 0 6-3.35 33 33 0 0 0 12.29 2.37 32.42 32.42 0 0 0 7.6-.89l.26-.1c.39-.22.77-.45 1.13-.68 15 1.83 31.23 10.14 33.1 11.11 1.29 1.83 12.53 18.78 8.26 49.43-3.5 25.12-14.26 48.75-21.39 64.38l-1.22 2.59c-12.92 30.79-7.82 70.52-5.08 91.86.24 1.85.46 3.56.65 5.11 2.25 18.67.58 28.27.57 28.36v.11c-1.68 24-16 42.5-34.87 44.92-12.06 1.55-24.36-3.76-33.74-14.57l-.1-.11-.35-.38A53.55 53.55 0 0 1 26 322q-.6-1.06-1.17-2.31a27.37 27.37 0 0 1-1.12-2.69c-2.21-6-3.7-13.6-4.43-22.64-.61-7.43-.7-16.18-.81-26.31 0-4.33-.1-8.88-.19-13.63-.05-1.64-.08-3.16-.1-4.52l-.1-3.44C17 218.84 7.59 165 7.5 164.43l-1.08-5.24c-1-4.94-1.83-8.83-2.35-11.6-2.15-11.54-1.29-22.43-.82-28.28z',
  [ESide.Right]:
    'M130.1 116a45.05 45.05 0 0 0-.88-5.67c-.3-1.53-.63-3.22-.91-5-.71-4.41-1.14-9.31-.14-13.43.32-1.34.72-2.7 1.14-4.14a61.89 61.89 0 0 0 1.93-7.76c.13-.8.22-1.6.28-2.41a22 22 0 0 0 0-2.44 17.4 17.4 0 0 0-.26-2.47 20.29 20.29 0 0 0-5.16-10.2c-1-1.14-9.7-10.95-15.49-10.1 0-4.14-.92-15.76-11.06-20.43-3.86-1.79-7.38-1.14-9.9 1.82a12.53 12.53 0 0 0-2.06 3.5C86.43 31.34 83.13 20 75.3 17.82c-4.4-1.23-8.47-.59-11.61 1.77C62.25 16.17 57.83 7.42 50 6.64c-2.51-.25-5 1.46-7.19 4.17C41.77 7 38.26.26 26.17 0 18.79-.17 10.78 6.19 5.75 16.18c-5.49 10.9-7.5 27.47 2.68 42.15l.26.37.15.21c.17.24.33.47.51.71C7 63-3.19 79.94 1 110.08c3.54 25.41 14.38 49.19 21.55 64.92l1.16 2.55c12.7 30.27 7.64 69.65 4.93 90.81-.24 1.85-.46 3.57-.65 5.12-2.23 18.47-.72 28-.56 28.9 1.76 25 16.81 44.18 36.6 46.72a36.1 36.1 0 0 0 4.4.28c11.27 0 22.42-5.51 31.16-15.58l.39-.42A55.65 55.65 0 0 0 107.3 323c.43-.75.84-1.57 1.23-2.43a28.59 28.59 0 0 0 1.21-2.95c2.27-6.14 3.8-13.93 4.55-23.15.61-7.49.71-16.28.82-26.45 0-4.32.09-8.87.18-13.6 0-1.65.08-3.17.1-4.53l.1-3.4c1.12-27.44 10.46-81.17 10.55-81.68l1.07-5.22c1-4.94 1.84-8.85 2.35-11.63 2.21-11.8 1.33-22.86.86-28.81-.11-1.32-.19-2.37-.22-3.15zM91.2 35c1.94-2.28 4.49-2.72 7.55-1.31 9.78 4.51 9.94 16.9 9.87 19.46a6.36 6.36 0 0 0-2.11 3.43 23.77 23.77 0 0 0-.39 10.79 24.54 24.54 0 0 1-14.94-10.58c-4.05-6.07-4.04-17.08.02-21.79zM74.77 19.74c9.12 2.56 11.43 20.92 11.45 21.1h.36a26.07 26.07 0 0 0 1 13.1c-1.58.37-8.34-1.32-15.3-3.94a17.55 17.55 0 0 1-11.67-14.39c-1-5.94.38-11 3.64-13.92 2.69-2.4 6.43-3.09 10.52-1.95zm-25-11.11C57 9.35 61.14 18.58 62.1 21c-3.18 3.44-4.45 8.79-3.47 14.89A19.52 19.52 0 0 0 64 47a31.15 31.15 0 0 1-17.65 1.09c-9.5-5.39-9.8-17.15-6.91-26.39 2.39-7.65 6.95-13.39 10.34-13.07zM7.53 17.08C12.1 8 19.45 2 25.91 2h.22c13.64.3 14.93 9.34 15.05 11.07a35.7 35.7 0 0 0-3.67 8.07C34.75 30 34.77 41 42.1 47.55c-13.69 2.15-27.84 9-31.23 10.77-13.34-17.9-5.95-36.05-3.34-41.24zm120 130.51c-.52 2.77-1.32 6.66-2.34 11.6l-1.09 5.24c-.09.54-9.45 54.41-10.58 82l-.1 3.44c0 1.36 0 2.88-.1 4.52-.09 4.75-.14 9.3-.18 13.63-.11 10.13-.21 18.88-.81 26.31-.74 9-2.23 16.65-4.44 22.64a25.24 25.24 0 0 1-1.14 2.77q-.57 1.25-1.17 2.31a53.55 53.55 0 0 1-7.07 10c-.12.14-.23.26-.35.38l-.09.11c-9.39 10.81-21.69 16.12-33.75 14.57-18.88-2.41-33.22-20.88-34.9-44.91v-.11c0-.09-1.69-9.69.57-28.36.18-1.55.4-3.26.64-5.11 2.74-21.34 7.84-61.07-5.08-91.86l-1.17-2.58C17.25 158.55 6.48 134.92 3 109.8c-4.26-30.65 7-47.6 8.26-49.43 1.87-1 18.13-9.28 33.1-11.11.37.23.74.46 1.14.68l.25.1a32.42 32.42 0 0 0 7.6.89 33 33 0 0 0 12.29-2.37 19.8 19.8 0 0 0 6 3.35c2.82 1.05 11.09 4 15.3 4a5.23 5.23 0 0 0 1.41-.17 17 17 0 0 0 1.19 2.15 26.46 26.46 0 0 0 16.85 11.55l.11-.52a30.85 30.85 0 0 0 13.1 18.3l1.08-1.69c-9.66-6.17-15-18.66-12.24-28.44.47-1.66 1.23-2.58 2.31-2.82 3.91-.86 11.34 6.5 13.87 9.42 7.29 8.17 5 16 2.77 23.54-.43 1.46-.83 2.84-1.17 4.23-1.5 6.2 0 13.75 1 19.27a41 41 0 0 1 .85 5.37c0 .8.12 1.87.22 3.21.5 5.85 1.37 16.74-.79 28.28z'
};

export class DrawSVG {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly data: TDatas;
  private readonly compareData?: any;
  private readonly vType: 'abs' | 'rel';

  private readonly ranges: TRangeData = {
    left: {
      data: {
        heelStrike: [EPos.P0],
        flatFoot: [EPos.P0],
        heelOff: [EPos.P0],
        toesOff: [EPos.P0]
      },
      compareData: {
        heelStrike: [EPos.P0],
        flatFoot: [EPos.P0],
        heelOff: [EPos.P0],
        toesOff: [EPos.P0]
      }
    },
    right: {
      data: {
        heelStrike: [EPos.P0],
        flatFoot: [EPos.P0],
        heelOff: [EPos.P0],
        toesOff: [EPos.P0]
      },
      compareData: {
        heelStrike: [EPos.P0],
        flatFoot: [EPos.P0],
        heelOff: [EPos.P0],
        toesOff: [EPos.P0]
      }
    }
  };

  private readonly config = {
    barHeight: {
      digitigrade: 85,
      plantigrade: 127.5,
      taligrade: 85
    },
    heelStrikePosition: {
      [EPos.P0]: 267.5,
      [EPos.P1]: 295,
      [EPos.P2]: 296,
      [EPos.P3]: 262
    },
    barPadding: 50,
    feetTranslation: 100,
    svgwidth: 132,
    padding: 80,
    middleCanvas: 0
  };

  private readonly average: TAverage = {
    range: 2.5,
    min: -19.5,
    max: -0.5,
    data: [
      {
        type: ERange.R1,
        min: -17.5,
        max: -12.5
      },
      {
        type: ERange.R2,
        min: -12.5,
        max: -7.5
      },
      {
        type: ERange.R3,
        min: -12.5,
        max: -7.5
      },
      {
        type: ERange.R4,
        min: -7.5,
        max: -2.5
      }
    ]
  };

  // Image
  private readonly image = {
    left: images.left,
    right: images.right
  };

  constructor(canvas: HTMLCanvasElement, data: TDatas, type: 'abs' | 'rel') {
    this.ctx = canvas.getContext('2d')!;
    this.canvas = canvas;
    this.data = data;
    this.vType = type;

    this.config.middleCanvas = this.canvas.width / 2;

    this.ctx.imageSmoothingEnabled = false;
  }

  async draw() {
    // If the context exist ...
    this.clean();
    const that: any = this;
    const image: any = {
      left: new Path2D(this.image.left),
      right: new Path2D(this.image.right)
    };

    const arrayData = this.compareData ? ['data', 'compareData'] : ['data'];
    for (const side of [ESide.Left, ESide.Right]) {
      for (const dataType of arrayData) {
        this.ranges[side][dataType].heelStrike = that[dataType].heelStrike[side];
        this.ranges[side][dataType].flatFoot = that[dataType].flatFoot[side];
        this.ranges[side][dataType].heelOff = that[dataType].heelOff[side];
        this.ranges[side][dataType].toesOff = that[dataType].toesOff[side];
      }

      // Draw the background image
      this.ctx.globalCompositeOperation = 'destination-over';
      this.ctx.setTransform(1, 0, 0, 1, 0, 0); // If not : when clear, canvas translate to the right

      if (side === ESide.Right) {
        this.ctx.fillStyle = '#2962fd';
        this.ctx.translate(this.canvas.scrollWidth - this.config.svgwidth * 0.8, 0);
      } else if (side === ESide.Left) {
        this.ctx.fillStyle = '#ffbd1a';
        this.ctx.translate(0, 0);
      }

      this.ctx.scale(0.8, 0.8);

      this.ctx.fill(image[side]);

      // Build chain
      this.lineBar(side)
        .curveRange(side)
        .lineResult(side)
        .pointResult(side)
        .shapeResult(side)
        .textSide(side)
        .resultPhases(side);
    }
  }

  lineBar(side: ESide): this {
    const posY = this.config.barPadding; // 60 + line width
    // General
    let vStart = 0;
    let vEnd = 0;
    // Data podding
    let paddingBaseline = 150;
    let paddingTaligrade = 117;
    let paddingDigitigrade = 200;

    // Align text dynamically
    this.ctx.textAlign = side;

    this.ctx.lineWidth = 1;
    this.ctx.strokeStyle = '#bebabf';
    this.ctx.font = '11px Montserrat';
    this.ctx.textBaseline = 'bottom';
    this.ctx.fillStyle = '#6d748c';
    this.ctx.textAlign = side;
    if (side === 'left') {
      vStart = 0 - this.config.feetTranslation;
      vEnd = this.config.middleCanvas + 50;
    } else {
      paddingBaseline *= -1;
      paddingTaligrade *= -1;
      paddingDigitigrade *= -1;
      vStart = this.canvas.scrollWidth / 2 + 30;
      vEnd = 0 - this.config.middleCanvas + this.config.svgwidth - 50;
    }

    // Digitigrade
    this.ctx.beginPath();
    this.ctx.lineTo(vStart + paddingDigitigrade, 0);
    this.ctx.lineTo(vEnd, 0);
    this.ctx.stroke();
    // Plantigrade
    this.ctx.beginPath();
    this.ctx.lineTo(vStart, posY + this.config.barHeight.digitigrade);
    this.ctx.lineTo(vEnd, posY + this.config.barHeight.digitigrade);
    this.ctx.stroke();
    // Taligrade
    this.ctx.beginPath();
    this.ctx.lineTo(
      vStart + paddingTaligrade,
      posY + this.config.barHeight.digitigrade + this.config.barHeight.plantigrade
    );
    this.ctx.lineTo(vEnd, posY + this.config.barHeight.digitigrade + this.config.barHeight.plantigrade);
    this.ctx.stroke();
    // BaseLine
    this.ctx.beginPath();
    this.ctx.lineTo(
      vStart + paddingBaseline,
      posY +
        this.config.barHeight.digitigrade +
        this.config.barHeight.plantigrade +
        this.config.barHeight.taligrade
    );
    this.ctx.lineTo(
      vEnd,
      posY +
        this.config.barHeight.digitigrade +
        this.config.barHeight.plantigrade +
        this.config.barHeight.taligrade
    );
    this.ctx.stroke();

    return this;
  }

  curveRange(side: ESide): this {
    // General
    const posY = this.config.barPadding; // 60 + line width
    const posX = {
      left: this.config.padding - this.config.feetTranslation,
      right: this.config.feetTranslation + 50
    };
    let index = 0;
    this.ctx.lineWidth = 1;
    this.ctx.strokeStyle = '#bebabf';
    // Data
    for (const range of Object.values(ERange)) {
      this.ctx.beginPath();
      // Heel off
      this.ctx.lineTo(
        posX[side] + this.config.svgwidth * getPrctValue(this.data.heelOff[range], side, this.average),
        posY + this.config.barHeight.digitigrade
      );
      // Flat foot
      this.ctx.lineTo(
        posX[side] + this.config.svgwidth * getPrctValue(this.data.flatFoot[range], side, this.average),
        posY + this.config.barHeight.digitigrade + this.config.barHeight.plantigrade
      );
      // Heel strike
      this.ctx.lineTo(
        posX[side] + this.config.svgwidth * getPrctValue(this.data.heelStrike[range], side, this.average),
        posY + this.config.heelStrikePosition[('P' + index) as EPos]
      );
      this.ctx.stroke();
      index += 1;
    }

    return this;
  }

  lineResult(side: ESide): this {
    // General
    const posY = this.config.barPadding; // 60 + line width
    const posX = {
      left: this.config.padding - this.config.feetTranslation,
      right: this.config.feetTranslation + 50
    };
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.lineWidth = 2;
    this.ctx.strokeStyle = '#5e6271';
    // Data
    this.ctx.setLineDash([]);
    this.ctx.beginPath();
    // Heel strike
    this.ctx.lineTo(
      posX[side] +
        this.config.svgwidth *
          getPrctValue(
            this.data.heelStrike[('range' + this.ranges[side].data.heelStrike.slice(1, 2)) as ERange],
            side,
            this.average
          ),
      posY + this.config.heelStrikePosition[('P' + this.ranges[side].data.heelStrike.slice(1, 2)) as EPos]
    );
    // Flat foot
    this.ctx.lineTo(
      posX[side] +
        this.config.svgwidth *
          getPrctValue(
            this.data.flatFoot[('range' + this.ranges[side].data.flatFoot.slice(1, 2)) as ERange],
            side,
            this.average
          ),
      posY + this.config.barHeight.digitigrade + this.config.barHeight.plantigrade
    );
    // Heel off
    this.ctx.lineTo(
      posX[side] +
        this.config.svgwidth *
          getPrctValue(
            this.data.heelOff[('range' + this.ranges[side].data.heelOff.slice(1, 2)) as ERange],
            side,
            this.average
          ),
      posY + this.config.barHeight.digitigrade
    );
    this.ctx.stroke();

    return this;
  }

  pointResult(side: ESide): this {
    // General
    const posY = this.config.barPadding; // 60 + line width
    const posX = {
      left: this.config.padding - this.config.feetTranslation,
      right: this.config.feetTranslation + 50
    };
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.lineWidth = 2;
    this.ctx.fillStyle = '#5e6271';
    // Data
    // Heel strike
    this.ctx.beginPath();

    this.ctx.arc(
      posX[side] +
        this.config.svgwidth *
          getPrctValue(
            this.data.heelStrike[('range' + this.ranges[side].data.heelStrike.slice(1, 2)) as ERange],
            side,
            this.average
          ),
      posY + this.config.heelStrikePosition[('P' + this.ranges[side].data.heelStrike.slice(1, 2)) as EPos],
      4,
      0,
      2 * Math.PI
    );
    this.ctx.fill();

    // Flat foot
    this.ctx.beginPath();
    this.ctx.arc(
      posX[side] +
        this.config.svgwidth *
          getPrctValue(
            this.data.flatFoot[('range' + this.ranges[side].data.flatFoot.slice(1, 2)) as ERange],
            side,
            this.average
          ),
      posY + this.config.barHeight.digitigrade + this.config.barHeight.plantigrade,
      4,
      0,
      2 * Math.PI
    );
    this.ctx.fill();

    // Heel off
    this.ctx.beginPath();
    this.ctx.arc(
      posX[side] +
        this.config.svgwidth *
          getPrctValue(
            this.data.heelOff[('range' + this.ranges[side].data.heelOff.slice(1, 2)) as ERange],
            side,
            this.average
          ),
      posY + this.config.barHeight.digitigrade,
      4,
      0,
      2 * Math.PI
    );
    this.ctx.fill();

    return this;
  }

  shapeResult(side: ESide): this {
    // General
    const posY = this.config.barPadding; // 60 + line width
    const posX = {
      left: this.config.padding - this.config.feetTranslation,
      right: this.config.feetTranslation + 50
    };
    // Toe off Shape
    this.ctx.fillStyle = '#5e6271';
    this.ctx.globalCompositeOperation = 'destination-over';

    this.ctx.beginPath();
    this.ctx.lineTo(
      posX[side] +
        this.config.svgwidth *
          getPrctValue(
            this.data.heelOff[('range' + this.ranges[side].data.heelOff.slice(1, 2)) as ERange],
            side,
            this.average
          ),
      posY + this.config.barHeight.digitigrade
    );
    if (this.ranges[side].data.toesOff === 0) {
      this.ctx.lineTo(getSymmetryValue(13, side), 85);
      this.ctx.lineTo(getSymmetryValue(25, side), 68);
      this.ctx.lineTo(getSymmetryValue(40, side), 60);
      this.ctx.lineTo(getSymmetryValue(45, side), 55);
      this.ctx.lineTo(getSymmetryValue(60, side), 50);
      this.ctx.fill();
    } else if (this.ranges[side].data.toesOff === 1) {
      this.ctx.lineTo(getSymmetryValue(65, side), 48);
      this.ctx.lineTo(getSymmetryValue(75, side), 50);
      this.ctx.lineTo(getSymmetryValue(90, side), 48);
      this.ctx.lineTo(getSymmetryValue(120, side), 58);
      this.ctx.fill();
    } else {
      this.ctx.lineTo(getSymmetryValue(120, side), 58);
      this.ctx.lineTo(getSymmetryValue(130, side), 80);
      this.ctx.lineTo(getSymmetryValue(130, side), 100);
      this.ctx.lineTo(getSymmetryValue(128, side), 120);
    }

    this.ctx.fill();
    return this;
  }

  resultPhases(side: ESide): this {
    if (side === 'left') {
      // General
      const that: any = this;
      const unit = this.vType === 'rel' ? '%' : 'ms';
      const phases = [
        {
          id: 'digitigrade',
          posY: 60,
          name: i18n.t('commons.standards.propulsion') as string,
          left: {
            value: this.data.digitigrade.left[this.vType],
            range: rangeColors[this.data.digitigrade.left[this.vType].status]
          },
          right: {
            value: this.data.digitigrade.right[this.vType],
            range: rangeColors[this.data.digitigrade.right[this.vType].status]
          }
        },
        {
          id: 'plantigrade',
          posY: 190,
          name: i18n.t('commons.standards.flat-foot') as string,
          left: {
            value: this.data.plantigrade.left[this.vType],
            range: rangeColors[this.data.plantigrade.left[this.vType].status]
          },
          right: {
            value: this.data.plantigrade.right[this.vType],
            range: rangeColors[this.data.plantigrade.right[this.vType].status]
          }
        },
        {
          id: 'taligrade',
          posY: 297,
          name: i18n.t('commons.standards.loading-value') as string,
          left: {
            value: this.data.taligrade.left[this.vType],
            range: rangeColors[this.data.taligrade.left[this.vType].status]
          },
          right: {
            value: this.data.taligrade.right[this.vType],
            range: rangeColors[this.data.taligrade.right[this.vType].status]
          }
        }
      ];
      const phasesImg: any = {
        digitigrade: {
          img: new Image(),
          imgSrc: require('@/assets/images/propulsion.svg')
        },
        plantigrade: {
          img: new Image(),
          imgSrc: require('@/assets/images/flat-foot.svg')
        },
        taligrade: {
          img: new Image(),
          imgSrc: require('@/assets/images/heel-strike.svg')
        }
      };
      const posYValues = 35;
      const posXValues = 60;
      const rect = {
        width: 70,
        height: 30
      };
      const middle = this.config.middleCanvas + 50;
      this.ctx.font = '600 11pt Montserrat';
      this.ctx.textAlign = 'center';
      const chevronsPos: any = {
        leftX: middle - posXValues - rect.width / 2 + (this.vType === 'rel' ? 5 : -2),
        rightX: middle + posXValues - rect.width / 2 + rect.width + (this.vType === 'rel' ? -16 : -8),
        up: -5,
        down: 5,
        posY: {
          up: 2,
          down: -3
        },
        padY: 17
      };

      for (const phase of phases) {
        // Img
        phasesImg[phase.id].img.addEventListener('load', () => {
          that.ctx!.drawImage(phasesImg[phase.id].img, middle, phase.posY, 15, 20);
        });

        phasesImg[phase.id].img.src = phasesImg[phase.id].imgSrc;

        // Name
        this.ctx.fillStyle = '#b2b4b4';
        this.ctx.fillText(phase.name, middle, phase.posY);
        // Values
        this.ctx.fillStyle = phase.left.range.color;
        this.ctx.fillText(`${phase.left.value.value}${unit}`, middle - posXValues, phase.posY + posYValues);
        this.ctx.fillStyle = phase.right.range.color;
        this.ctx.fillText(`${phase.right.value.value}${unit}`, middle + posXValues, phase.posY + posYValues);

        const leftPaddingChevron = phase.left.range.chevron ? 10 : 0;
        const rightPaddingChevron = phase.right.range.chevron ? 10 : 0;

        // Blocs
        this.ctx.fillStyle = phase.left.range.background;
        this.ctx.beginPath();
        this.ctx.rect(
          middle - posXValues - rect.width / 2 - leftPaddingChevron,
          phase.posY + posYValues - 25,
          rect.width + leftPaddingChevron,
          rect.height
        );
        this.ctx.fill();
        //
        this.ctx.fillStyle = phase.right.range.background;
        this.ctx.beginPath();
        this.ctx.rect(
          middle + posXValues - rect.width / 2,
          phase.posY + posYValues - 25,
          rect.width + rightPaddingChevron,
          rect.height
        );
        this.ctx.fill();

        this.ctx.lineWidth = 2;
        if (phase.left.range.chevron) {
          this.ctx.strokeStyle = phase.left.range.color;
          // Left
          this.ctx.beginPath();
          this.ctx.moveTo(
            chevronsPos.leftX,
            phase.posY + posYValues - 25 + chevronsPos.padY + chevronsPos.posY[phase.left.range.chevron]
          );
          this.ctx.lineTo(
            chevronsPos.leftX + 5,
            phase.posY +
              posYValues -
              25 +
              chevronsPos.padY +
              chevronsPos[phase.left.range.chevron] +
              chevronsPos.posY[phase.left.range.chevron]
          );
          this.ctx.lineTo(
            chevronsPos.leftX + 10,
            phase.posY + posYValues - 25 + chevronsPos.padY + chevronsPos.posY[phase.left.range.chevron]
          );
          this.ctx.stroke();
        }

        if (phase.right.range.chevron) {
          this.ctx.strokeStyle = phase.right.range.color;
          // Right
          this.ctx.beginPath();
          this.ctx.moveTo(
            chevronsPos.rightX,
            phase.posY + posYValues - 25 + chevronsPos.padY + chevronsPos.posY[phase.right.range.chevron]
          );
          this.ctx.lineTo(
            chevronsPos.rightX + 5,
            phase.posY +
              posYValues -
              25 +
              chevronsPos.padY +
              chevronsPos[phase.right.range.chevron] +
              chevronsPos.posY[phase.right.range.chevron]
          );
          this.ctx.lineTo(
            chevronsPos.rightX + 10,
            phase.posY + posYValues - 25 + chevronsPos.padY + chevronsPos.posY[phase.right.range.chevron]
          );
          this.ctx.stroke();
        }
      }
    }

    return this;
  }

  textSide(side: ESide): this {
    // Text general
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.font = 'bold 12pt Montserrat';
    this.ctx.textAlign = 'center';

    const text = i18n.t(`commons.standards.${side}`) as string;
    if (side === 'left') {
      this.ctx.fillStyle = '#ffbd1a';
      this.ctx.fillText(text.slice(0, 1), getSymmetryValue(20, side), 20);
    } else {
      this.ctx.fillStyle = '#2962fd';
      this.ctx.fillText(text.slice(0, 1), getSymmetryValue(20, side), 20);
    }

    return this;
  }

  clean() {
    // Store the current transformation matrix
    this.ctx.save();

    // Use the identity matrix while clearing the canvas
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Restore the transform
    this.ctx.restore();
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
